<script lang="ts">
  /**
   * One player in the spun squad.
   *
   * Height is the spec here: ~46px, so all thirteen are reachable in one short
   * scroll. The first prototype used 72px rows and every draft turned into a
   * scroll hunt instead of a decision. Nothing in this component may grow it.
   *
   * A blocked row always states its reason. A greyed card with no explanation
   * reads as a bug, and the feasibility guard already knows exactly why.
   */
  import type { Offer } from '@ipl-sim/engine-cricket';
  import { roleLabel, tier } from '../lib/format';

  interface Props {
    offer: Offer;
    /** True during the XI phase, when the batting range is meaningful. */
    showRange: boolean;
    selected?: boolean;
    onchoose: (offer: Offer) => void;
  }

  let { offer, showRange, selected = false, onchoose }: Props = $props();

  const p = $derived(offer.player);
  const blocked = $derived(Boolean(offer.blockedReason));

  /**
   * Keepers do not bowl, so showing them a BOWL figure of 22 invites the user
   * to compare a number that will never be used.
   */
  const figures = $derived(
    p.role === 'WK'
      ? [
          ['BAT', p.bat],
          ['POW', p.pow],
        ]
      : [
          ['BAT', p.bat],
          ['POW', p.pow],
          ['BOWL', p.bowl],
        ],
  ) as [string, number][];
</script>

<button
  class="prow"
  class:blocked
  class:sel={selected}
  data-role={p.role}
  disabled={blocked}
  onclick={() => onchoose(offer)}
>
  <span>
    <span class="nm">
      {p.name}
      {#if !p.isIndian}
        <svg class="plane" aria-hidden="true"><use href="#i-plane" /></svg>
        <span class="sr">Overseas</span>
      {/if}
    </span>
    <span class="sub">
      <span class="tagrole">{roleLabel(p.role)}</span>
      {#if showRange}<span class="pos">{p.posMin}–{p.posMax}</span>{/if}
    </span>
  </span>

  <span class="figs">
    {#each figures as [label, value] (label)}
      <span class="fig" data-t={tier(value)}><b>{value}</b><s>{label}</s></span>
    {/each}
  </span>

  {#if offer.blockedReason}
    <span class="why">{offer.blockedReason}</span>
  {/if}
</button>
